import { createContext, useContext } from 'react';
import type { Market, Selection } from '@/types/api';
import type { PriceChange } from './quoteSync';
import type { Leg } from './multi';

/*
  The slip's state lives ABOVE the router outlet, because the slip is rendered
  in more than one place and none of them is an ancestor of the others: the
  right rail in AppShell (desktop, `xl` and up) and the bottom sheet AppShell
  opens below `xl`, where the rail does not exist at all. A `useState` inside
  BetsPage cannot be read by either — that is exactly why clicking a selection
  appeared to do nothing on desktop, and why the sheet is driven from here too.

  Since 0049 the slip holds LEGS, not one selection. One leg is a single and is
  posted in the existing single shape; two to ten legs on different fixtures
  are a straight multi and are posted as `legs[]`. Same Game Multis are out —
  picking a second runner on a fixture already on the slip REPLACES that leg.
  `market`/`selection` remain as the FIRST leg for the parts of the shell that
  only need to know whether something is held.

  This module deliberately exports NO component. `react-refresh/only-export-
  components` is active (eslint.config.js extends reactRefresh.configs.vite),
  so a file exporting both the provider component and this hook would fail lint.
*/

export interface BetSlipValue {
  /** The held legs in slip order. Empty = nothing held. */
  legs: Leg[];
  /** First leg, kept for callers that only ask "is anything held?". */
  market: Market | null;
  selection: Selection | null;
  /** True with two or more legs — the ticket is a straight multi. */
  isMulti: boolean;
  /** Product of the held leg odds (preview; the server computes the exact decimal). */
  combinedOdds: number;
  dollarStake: string;
  successMsg: string;
  errorMsg: string;
  /**
   * Set when a held price was replaced by a fresher server price — either
   * from a board refetch or from a `quote_changed` refusal. The slip shows it
   * until the customer places, re-picks or clears. Null when nothing moved.
   */
  priceChange: PriceChange | null;
  /** True while a refused placement is being re-resolved against a fresh board. */
  isRefreshingQuote: boolean;
  /** Derived money, all in integer cents. */
  stakeCents: number;
  feeCents: number;
  feeBps: number;
  potentialPayout: number;
  insufficientFunds: boolean;
  /** Largest stake the server will accept on this ticket, previewed from /me. Null with nothing held. */
  maxStakeCents: number | null;
  /** Plain-words reason for the cap, for the hint under the stake field. */
  capReason: string | null;
  /** True when the typed stake exceeds the previewed cap. */
  overCap: boolean;
  canSubmit: boolean;
  isPending: boolean;
  /**
   * Below `xl` the slip is a bottom sheet. `pick` opens it; the sheet's own
   * close control and a successful placement close it. On desktop the sheet is
   * not rendered at all (CSS), so this flag is inert there.
   */
  sheetOpen: boolean;
  openSheet: () => void;
  closeSheet: () => void;
  /** Toggle a selection: add as a new leg, replace the leg on the same fixture, or remove it again. */
  pick: (market: Market, selection: Selection) => void;
  /** True when this selection is on the slip — the board uses it to highlight the odds button. */
  isPicked: (selectionId: string) => boolean;
  removeLeg: (selectionId: string) => void;
  clear: () => void;
  setStake: (value: string) => void;
  submit: () => void;
  dismissSuccess: () => void;
}

export const BetSlipContext = createContext<BetSlipValue | null>(null);

export function useBetSlip(): BetSlipValue {
  const ctx = useContext(BetSlipContext);
  if (!ctx) throw new Error('useBetSlip must be used inside <BetSlipProvider>');
  return ctx;
}
